import {
  OnOrderChange,
  ReorderableElement,
  ReorderableList,
} from '@monorepo/shared/componentsV2/dragAndDrop/ReorderableList';
import React, { useMemo } from 'react';
import { FieldTypeFactory } from './FieldTypeFactory';
import { FieldConditionHelpers } from './hooks/useFieldConditionHelpers';
import { EditableFormTemplateField } from './types';

interface ReorderableFormTemplateFieldsProps {
  fieldConditionHelpers: FieldConditionHelpers;
  openEditFieldModal: (fieldToEditId: string) => void;
  openEditFieldConditionsModal: (fieldToEditConditionsId: string) => void;
  openMoveInspectionDateModal: () => void;
  onCopyField: (fieldToCopy: EditableFormTemplateField) => void;
  onDeleteField: (fieldToDelete: EditableFormTemplateField) => void;
  sortedFields: EditableFormTemplateField[];
  updateFieldOrder: OnOrderChange;
  isEditingDisabled: boolean;
  getIsMovingDisabledAndReason: (field: EditableFormTemplateField) => {
    isMovingDisabled: boolean;
    movingDisabledReason: string;
  };
  getIsDeletingDisabledAndReason: (field: EditableFormTemplateField) => {
    isDeletingDisabled: boolean;
    deletingDisabledReason: string;
  };
}

export function ReorderableFormTemplateFieldsList({
  fieldConditionHelpers,
  openEditFieldModal,
  openEditFieldConditionsModal,
  openMoveInspectionDateModal,
  onCopyField,
  onDeleteField,
  sortedFields,
  updateFieldOrder,
  isEditingDisabled,
  getIsMovingDisabledAndReason,
  getIsDeletingDisabledAndReason,
}: ReorderableFormTemplateFieldsProps) {
  const orderedElements: ReorderableElement[] = useMemo(
    () =>
      sortedFields.map((field) => {
        const handleOpenEditFieldModal = () => openEditFieldModal(field.id);
        const handleOpenEditFieldConditionsModal = () =>
          openEditFieldConditionsModal(field.id);
        const handleCopyField = () => onCopyField(field);
        const handleDeleteField = () => onDeleteField(field);
        const { isMovingDisabled, movingDisabledReason } =
          getIsMovingDisabledAndReason(field);
        const { isDeletingDisabled, deletingDisabledReason } =
          getIsDeletingDisabledAndReason(field);
        return {
          id: field.id,
          component: (
            <FieldTypeFactory
              field={field}
              fieldConditionHelpers={fieldConditionHelpers}
              openEditFieldModal={handleOpenEditFieldModal}
              openEditFieldConditionsModal={handleOpenEditFieldConditionsModal}
              openMoveInspectionDateModal={openMoveInspectionDateModal}
              onCopyField={handleCopyField}
              onDeleteField={handleDeleteField}
              isEditingDisabled={isEditingDisabled}
              isMovingDisabled={isMovingDisabled}
              movingDisabledReason={movingDisabledReason}
              isDeletingDisabled={isDeletingDisabled}
              deletingDisabledReason={deletingDisabledReason}
            />
          ),
        };
      }),
    [
      sortedFields,
      getIsMovingDisabledAndReason,
      getIsDeletingDisabledAndReason,
      fieldConditionHelpers,
      openMoveInspectionDateModal,
      isEditingDisabled,
      openEditFieldModal,
      openEditFieldConditionsModal,
      onCopyField,
      onDeleteField,
    ],
  );

  return (
    orderedElements && (
      <ReorderableList
        orderedElements={orderedElements}
        onOrderChange={updateFieldOrder}
        uniqueDragListId="reorderableFormFields"
        listAriaLabel="Reorderable Form Fields"
        disableDragging={isEditingDisabled}
      />
    )
  );
}
